import {
  CALENDAR_HOURS,
  CalendarClock,
  CalendarPlacement,
  agendaOpeningRow,
  calendarSlotTime,
  cellKeyOf,
  formatCalendarHour,
  isCellFree,
  isCellGone
} from 'utils/socialCampaign/calendar'
import { CalendarPostCover, CalendarPostSummary } from './CalendarPost.component'
import { FC, KeyboardEvent, useId, useLayoutEffect, useRef } from 'react'

import { AddCircleOutlineOutlined } from '@mui/icons-material'
import { MUIBadge } from 'components/common/MUIBadge'
import { NetworkMark } from './NetworkMark.component'
import { STATUS_BADGE } from './CampaignPostRow.component'
import { formatCampaignDay } from 'utils/socialCampaign/campaignStatus'
import { formatRowDate } from 'utils/socialCampaign/schedule'
import styles from './CampaignCalendar.module.sass'
import { useTranslation } from 'react-i18next'

interface Props {
  days: string[]
  /** The picked day, one of `days` */
  day: string
  onDayChange: (day: string) => void
  /** Bumped by the calendar's bar: scrolls to where the day opens again */
  scrollRequest: number
  placements: CalendarPlacement[]
  cells: Map<string, CalendarPlacement[]>
  clock: CalendarClock
  summaryOf: (placement: CalendarPlacement) => CalendarPostSummary
  onOpenPost?: (postId: string) => void
  /** A free hour that has not gone creates a post there; none is offered without it */
  onAddPostAt?: (slot: { date: string, time: string }) => void
}

const STRIP_KEYS: Record<string, (index: number) => number> = {
  ArrowLeft: (index) => (index + 6) % 7,
  ArrowRight: (index) => (index + 1) % 7,
  Home: () => 0,
  End: () => 6,
}

/**
 * Up to 860px (prototype v3 `calendarHtml`, v1's `renderDay`): the strip of the week's seven days (a dot on a day with
 * posts) over the picked day, hour by hour. Each post is a card (cover, time, title, networks, the status as its
 * glyph); each free hour a "New post" line, visible at rest (no hover on a phone). Opens today on the hour in progress,
 * another day on its first post.
 */
export const CampaignCalendarDay: FC<Props> = ({ days, day, onDayChange, scrollRequest, placements, cells, clock, summaryOf, onOpenPost, onAddPostAt }) => {
  const { t, i18n } = useTranslation(['social_campaign'])
  const ids = `campaign-agenda-${useId().replace(/:/g, '')}`
  const stripRef = useRef<HTMLDivElement>(null)
  const agendaRef = useRef<HTMLDivElement>(null)
  const dayLabel = formatCampaignDay(day, i18n.language)

  // Scrolled to where the day opens when it shows or the bar asks, not each time a refetch brings its posts again
  useLayoutEffect(() => {
    const agenda = agendaRef.current
    const row = agenda?.querySelector<HTMLElement>(`[data-hour="${agendaOpeningRow(day, placements, clock)}"]`)
    if (agenda && row) agenda.scrollTop = row.offsetTop
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [day, scrollRequest])

  // The strip is one tab stop: the arrows walk the days, as tabs do
  const onStripKey = (event: KeyboardEvent<HTMLButtonElement>, index: number) => {
    const move = STRIP_KEYS[event.key]
    if (!move) return
    event.preventDefault()
    const next = move(index)
    onDayChange(days[next])
    stripRef.current?.querySelectorAll<HTMLButtonElement>('[role="tab"]')[next]?.focus()
  }

  const card = (placement: CalendarPlacement) => {
    const summary = summaryOf(placement)
    const badge = STATUS_BADGE[summary.status]
    const content = (
      <>
        <span className={styles.cardThumb}><CalendarPostCover post={placement.post} /></span>
        <span className={styles.cardMain}>
          <span className={styles.cardTime}>{placement.time}</span>
          <span className={styles.cardTitle}>{placement.post.subject}</span>
          <span className={styles.cardNets}>
            {placement.post.networks.map((network) => <NetworkMark key={network} network={network} size={16} />)}
          </span>
        </span>
        {/* the status as its glyph alone: the title needs the room, and the card's name says it */}
        <MUIBadge component="span" size="md" color={badge.color} icon={badge.icon} aria-hidden="true" sx={{ flex: 'none', gap: 0, paddingInline: '.4rem' }} />
      </>
    )
    return onOpenPost
      ? (
        <button
          key={placement.post.id}
          type="button"
          className={styles.card}
          data-post={placement.post.id}
          data-status={summary.status}
          aria-label={summary.label}
          onClick={() => onOpenPost(placement.post.id)}
        >
          {content}
        </button>
      )
      : (
        <div key={placement.post.id} className={styles.card} data-post={placement.post.id} data-status={summary.status} data-static="" role="img" aria-label={summary.label}>
          {content}
        </div>
      )
  }

  return (
    <div>

      <div ref={stripRef} className={styles.strip} role="tablist" aria-label={t('schedule.calendar.days')}>
        {days.map((item, index) => {
          const date = formatRowDate(item, i18n.language)
          const label = formatCampaignDay(item, i18n.language)
          const has = placements.some((placement) => placement.day === item)
          return (
            <button
              key={item}
              type="button"
              role="tab"
              id={`${ids}-${item}`}
              className={styles.stripDay}
              aria-selected={item === day}
              aria-controls={ids}
              tabIndex={item === day ? 0 : -1}
              aria-label={has ? t('schedule.calendar.has_posts', { date: label }) : label}
              data-today={item === clock.today || undefined}
              data-past={item < clock.today || undefined}
              onClick={() => onDayChange(item)}
              onKeyDown={(event) => onStripKey(event, index)}
            >
              <span className={styles.weekday} aria-hidden="true">{date.weekday.replace(/\.$/, '')}</span>
              <span className={styles.dayNumber} aria-hidden="true">{date.dayOfMonth}</span>
              <span className={styles.dot} aria-hidden="true" data-on={has} />
            </button>
          )
        })}
      </div>

      <div ref={agendaRef} className={styles.agenda} id={ids} role="tabpanel" aria-label={dayLabel}>
        {CALENDAR_HOURS.map((hour) => {
          const here = cells.get(cellKeyOf(day, hour)) ?? []
          const gone = isCellGone(day, hour, here, clock)
          return (
            <div key={hour} className={styles.agendaRow} data-past={gone || undefined} data-busy={here.length > 0 || undefined} data-hour={hour}>
              <span className={styles.agendaHour}>{formatCalendarHour(hour)}</span>
              <div className={styles.agendaSlot}>
                {here.map(card)}
                {isCellFree(day, hour, here, clock) && onAddPostAt &&
                  <button
                    type="button"
                    className={styles.agendaAdd}
                    aria-label={t('schedule.calendar.new_post_at', { date: dayLabel, time: formatCalendarHour(hour) })}
                    onClick={() => onAddPostAt({ date: day, time: calendarSlotTime(hour) })}
                  >
                    <AddCircleOutlineOutlined />
                    <span aria-hidden="true">{t('schedule.calendar.new_post')}</span>
                  </button>
                }
              </div>
            </div>
          )
        })}
      </div>

    </div>
  )
}
